import { inject, injectable } from 'inversify';

import { TYPES } from '@/engine/ioc';
import { CORE_TYPES } from '@/engine/app/modules/core';

import type { PlanningAlgorithm } from '@/engine/PlanningAlgorithm/PlanningAlgorithm';
import type { ViewsService } from '@/engine/app/Views.service';
import type { IObjectBoxesController, IObjectViewModel, IViewItemsViewModelController } from '@/engine/app/interfaces';
import type { ObservabilityLoggerService } from '@/app.obresvability/loggerService/observabilityLogger.service.ts';

import { SceneItemAction } from '@/engine/app/interfaces';
import type { NamedValue } from '@/engine/DataFlow/DataFlow';
import type { ICommandProcessor } from '@/engine/app/modules/core/interfaces/Command';
import { WithMixed, WithUndefined } from '@/components/viewOption/types';
import { AutoDisposer } from '@/engine/Utilities/AutoDisposer';

export interface ObjectBoxProps {
  label: string;
  objects: Array<IObjectViewModel>;
}

@injectable()
export class ObjectBoxesController implements IObjectBoxesController {
  private readonly _viewsService: ViewsService;

  private readonly _planningAlgorithm: PlanningAlgorithm;

  private readonly _viVmController: IViewItemsViewModelController;

  private readonly _loggerService: ObservabilityLoggerService;

  private readonly _commandProcessor: ICommandProcessor;

  public constructor(
    @inject(TYPES.ViewsService) viewsService: ViewsService,
    @inject(TYPES.IViewItemsViewModelController)
    viVmController: IViewItemsViewModelController,
    @inject(TYPES.PlanningAlgorithm) planningAlgorithm: PlanningAlgorithm,
    @inject(CORE_TYPES.ObservabilityLoggerService) loggerService: ObservabilityLoggerService,
    @inject(CORE_TYPES.CommandProcessor) commandProcessor: ICommandProcessor
  ) {
    this._viewsService = viewsService;
    this._planningAlgorithm = planningAlgorithm;
    this._viVmController = viVmController;
    this._loggerService = loggerService;
    this._commandProcessor = commandProcessor;
  }

  // @computed
  public getObjectBoxes(): Array<ObjectBoxProps> {
    return this._viewsService.currentView?.getObjectBoxes() || [];
  }

  public checkGroupViewItemsVisibility(descriptors: Array<string>): WithUndefined<WithMixed<boolean>> {
    if (descriptors.length === 0) {
      return undefined;
    }

    const allVisible = descriptors.every((d) => this._findObjects(d)?.isVisible === true);
    const allHidden = descriptors.every((d) => this._findObjects(d)?.isVisible === false);

    if (allVisible) {
      return true;
    }
    if (allHidden) {
      return false;
    }
    return 'mixed';
  }

  public checkGroupViewItemsBeingEdited(descriptors: Array<string>): WithUndefined<WithMixed<boolean>> {
    if (descriptors.length === 0) {
      return undefined;
    }

    const allBeingEdited = descriptors.every((d) => this._viVmController.isViewModelBeingEdited(d, false) !== false);
    const allNotBeingEdited = descriptors.every((d) => this._viVmController.isViewModelBeingEdited(d, false) !== true);

    if (allBeingEdited) {
      return true;
    }
    if (allNotBeingEdited) {
      return false;
    }
    return 'mixed';
  }

  public updateAllViewItemsVisibility(descriptors: Array<string>, isVisible: boolean): void {
    descriptors.forEach((descriptor) => {
      this._findObjects(descriptor)?.setVisibility(isVisible);
    });
  }

  public updateAllViewItemsBeingEdited(descriptors: Array<string>, isBeingEdited: boolean): void {
    descriptors.forEach((descriptor) => {
      this.setObjectBeingEdited(descriptor, isBeingEdited);
    });
  }

  private _findObjects(descriptor: string): IObjectViewModel | undefined {
    return this._viewsService.currentView?.getObjects().find((objs) => objs.descriptor == descriptor);
  }

  public setObjectVisibility(descriptor: string, visible: boolean): void {
    this._viVmController.setViewModelVisibility(descriptor, visible);
    const vm = this._findObjects(descriptor);
    if (vm) {
      this._loggerService.dispatchSceneItemAction(SceneItemAction.IndicationVisibilityChange, vm);
    }
  }

  public setObjectBeingEdited(descriptor: string, isBeingEdited: boolean): void {
    this._viVmController.setViewModelBeingEdited(descriptor, isBeingEdited, false);
    const identifier = `isBeingEdited$${descriptor}`;
    this._viewsService.getCurrentViewLifetime().remove(identifier);
    if (isBeingEdited) {
      this._viewsService
        .getCurrentViewLifetime()
        .set(
          identifier,
          new AutoDisposer(() => this._viVmController.setViewModelBeingEdited(descriptor, false, false))
        );
    }
  }

  public setObjectHighlighting(descriptor: string, highlighted: boolean): void {
    this._findObjects(descriptor)?.setHighlighting(highlighted);
  }

  public setObjectSelection(descriptor: string, selected: boolean): void {
    this._viVmController.setViewModelSelection(descriptor, selected);
  }

  public async isDefaultAsync(
    descriptor: string,
    approvedMeansDefault?: boolean,
    attachmentNumber?: number
  ): Promise<boolean> {
    const object = this._findObjects(descriptor);
    if (object) {
      const tuneDescriptor = attachmentNumber === undefined ? object.descriptor : object.attachments[attachmentNumber];
      return await this._planningAlgorithm.areTuneValuesDefaultAsync([tuneDescriptor], approvedMeansDefault ?? true);
    }
    return true;
  }

  public async resetToDefaultAsync(descriptor: string, attachmentNumber?: number): Promise<void> {
    const object = this._findObjects(descriptor);

    if (object) {
      const tuneDescriptor = attachmentNumber === undefined ? object.descriptor : object.attachments[attachmentNumber];
      const valueToUndo: NamedValue = [tuneDescriptor, await this._planningAlgorithm.getIValueAsync(tuneDescriptor)];
      await this._commandProcessor.executeAnonymousCommand(
        async (): Promise<undefined> => {
          await this._planningAlgorithm.resetTuneValuesAsync([tuneDescriptor]);
        },
        (): Promise<undefined> => {
          // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
          this._planningAlgorithm.setTuneValuesAsync([valueToUndo]);
          return Promise.resolve(undefined);
        }
      );
      this._findObjects(descriptor)?.setHighlighting(false);
    }
  }
}
